import countries from '../assets/constants/shopify/countries.json'
import { locales as contentLocales } from './constants'

import type { ContentLocale, StorefrontLocale } from './constants'

export type ShopifyCountry = {
  isoCode: string
  market?: { handle?: string | null } | null
  currency?: { isoCode?: string | null } | null
}

// Inlined by Vite: derivation happens at import time, so this cannot come from runtimeConfig.
declare const __STOREFRONT_LOCALES__: string | undefined

export type StorefrontLocaleMode =
  | { kind: 'off' }
  | { kind: 'markets'; handles: string[] }
  | { kind: 'auto' }
  | { kind: 'all' }

// Every country in these shares one price, so a locale each would be duplicate URLs.
const CATCH_ALL_MARKETS = ['united-states-and-row', 'eu']

let regionDisplayNames: Intl.DisplayNames | null | undefined

function countryNameFor(isoCode: string): string {
  if (regionDisplayNames === undefined) {
    try {
      regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' })
    } catch {
      regionDisplayNames = null
    }
  }

  try {
    return regionDisplayNames?.of(isoCode) || isoCode
  } catch {
    return isoCode
  }
}

export function resolveStorefrontLocaleMode(raw?: string | null): StorefrontLocaleMode {
  const value = (raw || '').trim().toLowerCase()

  if (value === 'auto') return { kind: 'auto' }
  if (value === '*' || value === 'all') return { kind: 'all' }

  if (value.startsWith('markets:')) {
    const handles = value
      .slice('markets:'.length)
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean)

    return handles.length ? { kind: 'markets', handles } : { kind: 'off' }
  }

  // Unrecognised is off, so a typo cannot publish a URL space.
  return { kind: 'off' }
}

function rawMode(): string {
  if (typeof __STOREFRONT_LOCALES__ !== 'undefined') return __STOREFRONT_LOCALES__ || ''

  return (typeof process !== 'undefined' && process.env?.STOREFRONT_LOCALES) || ''
}

export const storefrontLocaleMode: StorefrontLocaleMode = resolveStorefrontLocaleMode(rawMode())

function marketIsDerivable(handle: string | null | undefined, mode: StorefrontLocaleMode): boolean {
  if (!handle) return false

  switch (mode.kind) {
    case 'all':
      return true
    case 'auto':
      return !CATCH_ALL_MARKETS.includes(handle)
    case 'markets':
      return mode.handles.includes(handle)
    default:
      return false
  }
}

export function representativeFor(
  currency: string,
  locales: ContentLocale[] = contentLocales
): ContentLocale['code'] {
  return locales.find((locale) => locale.currency === currency)?.code ?? 'en-gb'
}

export function deriveStorefrontLocales(
  countryList: ShopifyCountry[],
  locales: ContentLocale[] = contentLocales,
  mode: StorefrontLocaleMode = storefrontLocaleMode
): StorefrontLocale[] {
  const byCode = new Map<string, StorefrontLocale>()

  countryList.forEach((country) => {
    const currency = country.currency?.isoCode

    if (!country.isoCode || !currency) return
    if (!marketIsDerivable(country.market?.handle, mode)) return

    const countryCode = country.isoCode.toLowerCase()

    byCode.set(`en-${countryCode}`, {
      code: `en-${countryCode}`,
      iso: `en-${country.isoCode.toUpperCase()}`,
      dir: 'ltr',
      countryCode,
      lang: 'en',
      countryName: countryNameFor(country.isoCode),
      currency,
      dato: false,
      shopifyMarketHandle: country.market?.handle || '',
      contentLocale: representativeFor(currency, locales),
    })
  })

  // Overwrites anything derived for the same country, and is the only pass that
  // produces en-eu, which has no country of its own.
  locales.forEach((locale) => {
    byCode.set(locale.code, { ...locale, contentLocale: locale.code })
  })

  return [...byCode.values()]
}

export const storefrontLocales: StorefrontLocale[] = deriveStorefrontLocales(countries as ShopifyCountry[])
